<script lang="ts" setup>
import { ref, computed } from 'vue'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import LockIcon from '@lahaus-roomie/roomie/src/assets/icons/lock.svg'
import IconLocked from '@lahaus-roomie/roomie/src/assets/icons/v2/locked.svg'
import UrbanIcon from '@lahaus-roomie/roomie/src/assets/icons/urban.svg'
import EarningsIcon from '@lahaus-roomie/roomie/src/assets/icons/earnings.svg'
import type { Props } from './types'
import UserIdentityContainer from '@/components/App/UserIdentityContainer/index.vue'
import { LazyAppModal } from '#components'
import { useThemeStore } from '@/stores/useThemeStore'

const themeStore = useThemeStore()

const props = withDefaults(defineProps<Props>(), {
  showGeneralTitle: false,
  shouldEnqueueTwilioContact: true
})
const emit = defineEmits(['registration-submit'])
const { t } = useI18n()
const { $useLhId } = useNuxtApp()

const isRegistrationModalOpen = ref(false)

const title = computed(() => props.showGeneralTitle ? t('title.general') : t('title.singular'))
</script>

<script lang="ts">
export default {
  name: 'LockOverlay'
}
</script>

<template>
  <div class="lock-overlay flex flex-col justify-end md:justify-center items-center w-full p-20">
    <Teleport to="body">
      <LazyAppModal
        v-if="isRegistrationModalOpen"
        class="lock-overlay__registration-modal !fixed top-0 z-60"
        :close-tracking-lh-id="$useLhId({ P: screen, Z: 'bloqueo de pantalla', A: 'cerrar_modal', C: 'cerrar_icon' })"
        @close="isRegistrationModalOpen = false">
        <UserIdentityContainer
          :id="`${screen}-user-identity-container`"
          :listing-id
          :listing-ids
          :hub-code
          :screen
          :show-general-title
          :should-enqueue-twilio-contact
          :data-attributes="{ dataLh: {P: screen, Z: 'bloqueo de pantalla'}}"
          @submit="(form) => emit('registration-submit', form)" />
      </LazyAppModal>
    </Teleport>

    <div class="lock-overlay__content mb-50 lg:mb-0">
      <div class="lock-overlay__icon-divider flex items-center w-full gap-6 mb-10 md:mb-24">
        <div
          :class="{
            'border-carbon-light-300': !themeStore.isV2
          }"
          class="divider-lock border rounded-circle w-48 h-48 flex justify-center items-center shrink-0">
          <IconLocked
            v-if="themeStore.isV2" />

          <LockIcon
            v-else
            class="fill-carbon-800" />
        </div>
      </div>

      <div class="text-center">
        <h1
          v-sanitize.basic="title"
          class="mb-32"
          :class="{
            'text-24 md:text-40 font-bold': !themeStore.isV2,
            'hl-2-sb md:hl-1-sb text-pine-600': themeStore.isV2
          }" />

        <div class="px-12">
          <ul
            :class="{
              'text-14 md:text-16 text-carbon-600': !themeStore.isV2,
              'options hl-3-m text-pine-600 opacity-40': themeStore.isV2
            }"
            class="mb-24 flex flex-col lg:flex-row lg:justify-center gap-8 md:gap-24">
            <li class="inline-flex gap-10 text-left">
              <UrbanIcon
                v-if="!themeStore.isV2"
                class="w-20 h-20 fill-carbon-800" />

              <p>{{ t('features.projects') }}</p>
            </li>

            <li class="inline-flex gap-10 text-left">
              <EarningsIcon
                v-if="!themeStore.isV2"
                class="w-20 h-20 fill-carbon-800" />

              <p>{{ t('features.paymentPlans') }}</p>
            </li>

            <li
              v-if="themeStore.isV2"
              class="inline-flex gap-10 text-left">
              <p>{{ t('features.legal') }}</p>
            </li>
          </ul>
        </div>

        <RoomieButton
          :id="`${screen}-lock-overlay-cta`"
          :data-lh-id="$useLhId({ P: screen, Z: 'bloqueo de pantalla', A: 'abrir_modal', C: t('cta.label') })"
          type="button"
          :variant="themeStore.isV2 ? 'primary' :'filled'"
          class="text-16 w-full lg:w-fit"
          @click="isRegistrationModalOpen = true">
          {{ t('cta.label') }}
        </RoomieButton>
      </div>
    </div>
  </div>
</template>

<i18n src="./i18n.json" lang="json" />

<style lang="scss" src="./_index.scss" />
